import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    useTheme,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    InputAdornment,
    Pagination,
    Stack,
    CircularProgress,
} from '@mui/material';
import { Plus, Edit2, Trash2, Search, X, AlertCircle, CheckCircle } from 'lucide-react';
import { Layout } from '@layout/Layout';
import { useAppTheme } from '@contexts/ThemeContext';
import * as AssessmentService from '@services/AssessmentService';
import Spinner from './components/Spinner';
import './ManageDimensionsModules.css';

const ManageDimensionsModules: React.FC = () => {
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();

    // State for dimensions
    const [allDimensions, setAllDimensions] = useState<AssessmentService.Dimension[]>([]);
    const [dimensionSearchQuery, setDimensionSearchQuery] = useState('');
    const [dimensionCurrentPage, setDimensionCurrentPage] = useState(1);
    const dimensionsPerPage = 10;

    // State for modules
    const [allModules, setAllModules] = useState<AssessmentService.Module[]>([]);
    const [moduleSearchQuery, setModuleSearchQuery] = useState('');
    const [moduleCurrentPage, setModuleCurrentPage] = useState(1);
    const modulesPerPage = 10;

    // Chart types for dimension creation/editing
    const [chartTypes, setChartTypes] = useState<AssessmentService.ChartType[]>([]);

    // Loading and error states
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Modal states for dimensions
    const [dimensionModalOpen, setDimensionModalOpen] = useState(false);
    const [dimensionEditMode, setDimensionEditMode] = useState(false);
    const [currentDimension, setCurrentDimension] = useState<AssessmentService.Dimension | null>(null);
    const [dimensionFormData, setDimensionFormData] = useState({
        dimension_name: '',
        chart_id: '' as number | ''
    });

    // Modal states for modules
    const [moduleModalOpen, setModuleModalOpen] = useState(false);
    const [moduleEditMode, setModuleEditMode] = useState(false);
    const [currentModule, setCurrentModule] = useState<AssessmentService.Module | null>(null);
    const [moduleFormData, setModuleFormData] = useState({
        title: ''
    });

    // Delete confirmation modals
    const [dimensionDeleteModalOpen, setDimensionDeleteModalOpen] = useState(false);
    const [dimensionToDelete, setDimensionToDelete] = useState<AssessmentService.Dimension | null>(null);
    const [moduleDeleteModalOpen, setModuleDeleteModalOpen] = useState(false);
    const [moduleToDelete, setModuleToDelete] = useState<AssessmentService.Module | null>(null);

    // Action loading states
    const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        fetchAll();
    }, []);

    const fetchAll = async () => {
        setLoading(true);
        setError(null);
        try {
            const [dimensionsRes, modulesRes, chartTypesRes] = await Promise.all([
                AssessmentService.getDimensions(),
                AssessmentService.getModules(),
                AssessmentService.getChartTypes()
            ]);

            if (dimensionsRes.success && dimensionsRes.data) {
                setAllDimensions(dimensionsRes.data);
            }
            if (modulesRes.success && modulesRes.data) {
                setAllModules(modulesRes.data);
            }
            if (chartTypesRes.success && chartTypesRes.data) {
                setChartTypes(chartTypesRes.data);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    // Filter and paginate dimensions
    const filteredDimensions = useMemo(() => {
        if (!dimensionSearchQuery.trim()) {
            return allDimensions;
        }
        return allDimensions.filter(dim =>
            dim.dimension_name.toLowerCase().includes(dimensionSearchQuery.toLowerCase())
        );
    }, [allDimensions, dimensionSearchQuery]);

    const paginatedDimensions = useMemo(() => {
        const startIndex = (dimensionCurrentPage - 1) * dimensionsPerPage;
        const endIndex = startIndex + dimensionsPerPage;
        return filteredDimensions.slice(startIndex, endIndex);
    }, [filteredDimensions, dimensionCurrentPage, dimensionsPerPage]);

    const dimensionTotalPages = Math.ceil(filteredDimensions.length / dimensionsPerPage);

    // Filter and paginate modules
    const filteredModules = useMemo(() => {
        if (!moduleSearchQuery.trim()) {
            return allModules;
        }
        return allModules.filter(mod =>
            mod.title.toLowerCase().includes(moduleSearchQuery.toLowerCase())
        );
    }, [allModules, moduleSearchQuery]);

    const paginatedModules = useMemo(() => {
        const startIndex = (moduleCurrentPage - 1) * modulesPerPage;
        const endIndex = startIndex + modulesPerPage;
        return filteredModules.slice(startIndex, endIndex);
    }, [filteredModules, moduleCurrentPage, modulesPerPage]);

    const moduleTotalPages = Math.ceil(filteredModules.length / modulesPerPage);

    // Reset to page 1 when search query changes
    useEffect(() => {
        setDimensionCurrentPage(1);
    }, [dimensionSearchQuery]);

    useEffect(() => {
        setModuleCurrentPage(1);
    }, [moduleSearchQuery]);

    // Dimension handlers
    const handleOpenDimensionModal = (dimension?: AssessmentService.Dimension) => {
        if (dimension) {
            setDimensionEditMode(true);
            setCurrentDimension(dimension);
            setDimensionFormData({
                dimension_name: dimension.dimension_name,
                chart_id: dimension.chart_id || ''
            });
        } else {
            setDimensionEditMode(false);
            setCurrentDimension(null);
            setDimensionFormData({
                dimension_name: '',
                chart_id: ''
            });
        }
        setDimensionModalOpen(true);
    };

    const handleCloseDimensionModal = () => {
        setDimensionModalOpen(false);
        setDimensionEditMode(false);
        setCurrentDimension(null);
        setDimensionFormData({
            dimension_name: '',
            chart_id: ''
        });
    };

    const handleSaveDimension = async () => {
        const key = 'save-dimension';
        setActionLoading(prev => ({ ...prev, [key]: true }));
        setError(null);
        setSuccessMessage(null);

        // Track start time to ensure minimum 1 second loading
        const startTime = Date.now();

        try {
            const data: AssessmentService.CreateDimensionData = {
                dimension_name: dimensionFormData.dimension_name,
                chart_id: dimensionFormData.chart_id || undefined
            };

            let response;
            if (dimensionEditMode && currentDimension) {
                response = await AssessmentService.updateDimension(currentDimension.dimension_id, data);
            } else {
                response = await AssessmentService.createDimension(data);
            }

            // Calculate remaining time to meet minimum 1 second
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 1000 - elapsedTime);

            // Wait for remaining time to ensure minimum 1 second loading
            await new Promise(resolve => setTimeout(resolve, remainingTime));

            if (response.success) {
                setActionLoading(prev => ({ ...prev, [key]: false }));
                setSuccessMessage(dimensionEditMode ? 'Dimension updated successfully' : 'Dimension created successfully');
                await fetchAll();
                handleCloseDimensionModal();

                // Auto-hide success message after 1 second
                setTimeout(() => setSuccessMessage(null), 1000);
            } else {
                throw new Error(response.error?.message || 'Failed to save dimension');
            }
        } catch (err: any) {
            // Calculate remaining time to meet minimum 1 second
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 1000 - elapsedTime);

            // Wait for remaining time
            await new Promise(resolve => setTimeout(resolve, remainingTime));

            setActionLoading(prev => ({ ...prev, [key]: false }));
            setError(err.message);

            // Auto-hide error after 1 second
            setTimeout(() => setError(null), 1000);
        }
    };

    const handleOpenDimensionDeleteModal = (dimension: AssessmentService.Dimension) => {
        setDimensionToDelete(dimension);
        setDimensionDeleteModalOpen(true);
    };

    const handleCloseDimensionDeleteModal = () => {
        setDimensionToDelete(null);
        setDimensionDeleteModalOpen(false);
    };

    const handleConfirmDeleteDimension = async () => {
        if (!dimensionToDelete) return;

        const key = 'delete-dimension';
        setActionLoading(prev => ({ ...prev, [key]: true }));
        setError(null);
        setSuccessMessage(null);

        // Track start time to ensure minimum 1 second loading
        const startTime = Date.now();

        try {
            const response = await AssessmentService.deleteDimension(dimensionToDelete.dimension_id);

            // Calculate remaining time to meet minimum 1 second
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 1000 - elapsedTime);

            // Wait for remaining time to ensure minimum 1 second loading
            await new Promise(resolve => setTimeout(resolve, remainingTime));

            if (response.success) {
                setActionLoading(prev => ({ ...prev, [key]: false }));
                setSuccessMessage('Dimension deleted successfully');
                await fetchAll();
                handleCloseDimensionDeleteModal();

                // Auto-hide success message after 1 second
                setTimeout(() => setSuccessMessage(null), 1000);
            } else {
                throw new Error(response.error?.message || 'Failed to delete dimension');
            }
        } catch (err: any) {
            // Calculate remaining time to meet minimum 1 second
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 1000 - elapsedTime);

            // Wait for remaining time
            await new Promise(resolve => setTimeout(resolve, remainingTime));

            setActionLoading(prev => ({ ...prev, [key]: false }));
            setError(err.message);

            // Auto-hide error after 1 second
            setTimeout(() => setError(null), 1000);
        }
    };

    // Module handlers
    const handleOpenModuleModal = (module?: AssessmentService.Module) => {
        if (module) {
            setModuleEditMode(true);
            setCurrentModule(module);
            setModuleFormData({
                title: module.title
            });
        } else {
            setModuleEditMode(false);
            setCurrentModule(null);
            setModuleFormData({
                title: ''
            });
        }
        setModuleModalOpen(true);
    };

    const handleCloseModuleModal = () => {
        setModuleModalOpen(false);
        setModuleEditMode(false);
        setCurrentModule(null);
        setModuleFormData({
            title: ''
        });
    };

    const handleSaveModule = async () => {
        const key = 'save-module';
        setActionLoading(prev => ({ ...prev, [key]: true }));
        setError(null);
        setSuccessMessage(null);

        // Track start time to ensure minimum 1 second loading
        const startTime = Date.now();

        try {
            const data: AssessmentService.CreateModuleData = {
                title: moduleFormData.title
            };

            let response;
            if (moduleEditMode && currentModule) {
                response = await AssessmentService.updateModule(currentModule.moduleId, data);
            } else {
                response = await AssessmentService.createModule(data);
            }

            // Calculate remaining time to meet minimum 1 second
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 1000 - elapsedTime);

            // Wait for remaining time to ensure minimum 1 second loading
            await new Promise(resolve => setTimeout(resolve, remainingTime));

            if (response.success) {
                setActionLoading(prev => ({ ...prev, [key]: false }));
                setSuccessMessage(moduleEditMode ? 'Module updated successfully' : 'Module created successfully');
                await fetchAll();
                handleCloseModuleModal();

                // Auto-hide success message after 1 second
                setTimeout(() => setSuccessMessage(null), 1000);
            } else {
                throw new Error(response.error?.message || 'Failed to save module');
            }
        } catch (err: any) {
            // Calculate remaining time to meet minimum 1 second
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 1000 - elapsedTime);

            // Wait for remaining time
            await new Promise(resolve => setTimeout(resolve, remainingTime));

            setActionLoading(prev => ({ ...prev, [key]: false }));
            setError(err.message);

            // Auto-hide error after 1 second
            setTimeout(() => setError(null), 1000);
        }
    };

    const handleOpenModuleDeleteModal = (module: AssessmentService.Module) => {
        setModuleToDelete(module);
        setModuleDeleteModalOpen(true);
    };

    const handleCloseModuleDeleteModal = () => {
        setModuleToDelete(null);
        setModuleDeleteModalOpen(false);
    };

    const handleConfirmDeleteModule = async () => {
        if (!moduleToDelete) return;

        const key = 'delete-module';
        setActionLoading(prev => ({ ...prev, [key]: true }));
        setError(null);
        setSuccessMessage(null);

        // Track start time to ensure minimum 1 second loading
        const startTime = Date.now();

        try {
            const response = await AssessmentService.deleteModule(moduleToDelete.moduleId);

            // Calculate remaining time to meet minimum 1 second
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 1000 - elapsedTime);

            // Wait for remaining time to ensure minimum 1 second loading
            await new Promise(resolve => setTimeout(resolve, remainingTime));

            if (response.success) {
                setActionLoading(prev => ({ ...prev, [key]: false }));
                setSuccessMessage('Module deleted successfully');
                await fetchAll();
                handleCloseModuleDeleteModal();

                // Auto-hide success message after 1 second
                setTimeout(() => setSuccessMessage(null), 1000);
            } else {
                throw new Error(response.error?.message || 'Failed to delete module');
            }
        } catch (err: any) {
            // Calculate remaining time to meet minimum 1 second
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 1000 - elapsedTime);

            // Wait for remaining time
            await new Promise(resolve => setTimeout(resolve, remainingTime));

            setActionLoading(prev => ({ ...prev, [key]: false }));
            setError(err.message);

            // Auto-hide error after 1 second
            setTimeout(() => setError(null), 1000);
        }
    };

    const getChartName = (chartId: number | null) => {
        if (!chartId) return 'N/A';
        const chart = chartTypes.find(c => c.chart_id === chartId);
        return chart ? chart.chart_name : 'Unknown';
    };

    if (loading) {
        return (
            <Layout>
                <div data-theme={isDarkMode ? 'dark' : 'light'}>
                    <Spinner />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <Box sx={{
                backgroundColor: theme.palette.background.default,
                borderRadius: '8px',
                margin: { xs: '8px', sm: '16px' },
                minHeight: '100vh'
            }}>
                {/* Success/Error Messages */}
                {error && (
                    <div className="alert alert-error" data-theme={isDarkMode ? 'dark' : 'light'}>
                        <AlertCircle size={20} />
                        <span>{error}</span>
                    </div>
                )}

                {successMessage && (
                    <div className="alert alert-success" data-theme={isDarkMode ? 'dark' : 'light'}>
                        <CheckCircle size={20} />
                        <span>{successMessage}</span>
                    </div>
                )}

                {/* Header */}
                <Box mb={4}>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            color: theme.palette.text.primary,
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                            fontWeight: { xs: 600, sm: 700 },
                            lineHeight: { xs: 1.2, sm: 1.3 },
                            mb: 1
                        }}
                    >
                        Manage Dimensions & Modules
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: theme.palette.text.secondary,
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                    >
                        Add, edit, and delete dimensions and modules for assessments
                    </Typography>
                </Box>

                {/* Dimensions Section */}
                <Box mb={6}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                            Dimensions ({allDimensions.length})
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Plus size={20} />}
                            onClick={() => handleOpenDimensionModal()}
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                '&:hover': { backgroundColor: theme.palette.primary.dark }
                            }}
                        >
                            Add Dimension
                        </Button>
                    </Box>

                    {/* Search Bar */}
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search dimensions..."
                            value={dimensionSearchQuery}
                            onChange={(e) => setDimensionSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search size={20} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                maxWidth: '500px',
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#fff',
                                }
                            }}
                        />
                        {dimensionSearchQuery && (
                            <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                                Found {filteredDimensions.length} dimension(s)
                            </Typography>
                        )}
                    </Box>

                    {/* Dimensions Table */}
                    <Card sx={{
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
                        borderRadius: 2,
                        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : `1px solid ${theme.palette.divider}`,
                        mb: 3
                    }}>
                        <CardContent sx={{ p: 0 }}>
                            <TableContainer component={Paper} sx={{
                                backgroundColor: 'transparent',
                                boxShadow: 'none'
                            }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{
                                                backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                                                color: theme.palette.text.primary,
                                                fontWeight: 700,
                                                fontSize: '0.95rem'
                                            }}>
                                                ID
                                            </TableCell>
                                            <TableCell sx={{
                                                backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                                                color: theme.palette.text.primary,
                                                fontWeight: 700,
                                                fontSize: '0.95rem'
                                            }}>
                                                Dimension Name
                                            </TableCell>
                                            <TableCell sx={{
                                                backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                                                color: theme.palette.text.primary,
                                                fontWeight: 700,
                                                fontSize: '0.95rem'
                                            }}>
                                                Chart Type
                                            </TableCell>
                                            <TableCell sx={{
                                                backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                                                color: theme.palette.text.primary,
                                                fontWeight: 700,
                                                fontSize: '0.95rem',
                                                textAlign: 'center'
                                            }}>
                                                Actions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedDimensions.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} sx={{
                                                    textAlign: 'center',
                                                    padding: '40px',
                                                    color: theme.palette.text.secondary
                                                }}>
                                                    {dimensionSearchQuery ? 'No dimensions found matching your search' : 'No dimensions found'}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            paginatedDimensions.map((dimension) => (
                                                <TableRow
                                                    key={dimension.dimension_id}
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: isDarkMode
                                                                ? 'rgba(255, 255, 255, 0.05)'
                                                                : 'rgba(0, 0, 0, 0.02)',
                                                        },
                                                        transition: 'background-color 0.2s ease'
                                                    }}
                                                >
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>
                                                        {dimension.dimension_id}
                                                    </TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>
                                                        {dimension.dimension_name}
                                                    </TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>
                                                        {getChartName(dimension.chart_id)}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenDimensionModal(dimension)}
                                                            sx={{ color: theme.palette.primary.main, mr: 1 }}
                                                        >
                                                            <Edit2 size={18} />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenDimensionDeleteModal(dimension)}
                                                            sx={{ color: theme.palette.error.main }}
                                                        >
                                                            <Trash2 size={18} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>

                    {/* Pagination */}
                    {filteredDimensions.length > 0 && dimensionTotalPages > 1 && (
                        <Stack spacing={2} alignItems="center" sx={{ mt: 3 }}>
                            <Pagination
                                count={dimensionTotalPages}
                                page={dimensionCurrentPage}
                                onChange={(e, value) => setDimensionCurrentPage(value)}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                            />
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Showing {((dimensionCurrentPage - 1) * dimensionsPerPage) + 1} - {Math.min(dimensionCurrentPage * dimensionsPerPage, filteredDimensions.length)} of {filteredDimensions.length} dimensions
                            </Typography>
                        </Stack>
                    )}
                </Box>

                {/* Modules Section */}
                <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                            Modules ({allModules.length})
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Plus size={20} />}
                            onClick={() => handleOpenModuleModal()}
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                '&:hover': { backgroundColor: theme.palette.primary.dark }
                            }}
                        >
                            Add Module
                        </Button>
                    </Box>

                    {/* Search Bar */}
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search modules..."
                            value={moduleSearchQuery}
                            onChange={(e) => setModuleSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search size={20} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                maxWidth: '500px',
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#fff',
                                }
                            }}
                        />
                        {moduleSearchQuery && (
                            <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                                Found {filteredModules.length} module(s)
                            </Typography>
                        )}
                    </Box>

                    {/* Modules Table */}
                    <Card sx={{
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
                        borderRadius: 2,
                        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : `1px solid ${theme.palette.divider}`,
                        mb: 3
                    }}>
                        <CardContent sx={{ p: 0 }}>
                            <TableContainer component={Paper} sx={{
                                backgroundColor: 'transparent',
                                boxShadow: 'none'
                            }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{
                                                backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                                                color: theme.palette.text.primary,
                                                fontWeight: 700,
                                                fontSize: '0.95rem'
                                            }}>
                                                ID
                                            </TableCell>
                                            <TableCell sx={{
                                                backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                                                color: theme.palette.text.primary,
                                                fontWeight: 700,
                                                fontSize: '0.95rem'
                                            }}>
                                                Module Title
                                            </TableCell>
                                            <TableCell sx={{
                                                backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                                                color: theme.palette.text.primary,
                                                fontWeight: 700,
                                                fontSize: '0.95rem',
                                                textAlign: 'center'
                                            }}>
                                                Actions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedModules.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} sx={{
                                                    textAlign: 'center',
                                                    padding: '40px',
                                                    color: theme.palette.text.secondary
                                                }}>
                                                    {moduleSearchQuery ? 'No modules found matching your search' : 'No modules found'}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            paginatedModules.map((module) => (
                                                <TableRow
                                                    key={module.moduleId}
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: isDarkMode
                                                                ? 'rgba(255, 255, 255, 0.05)'
                                                                : 'rgba(0, 0, 0, 0.02)',
                                                        },
                                                        transition: 'background-color 0.2s ease'
                                                    }}
                                                >
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>
                                                        {module.moduleId}
                                                    </TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>
                                                        {module.title}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenModuleModal(module)}
                                                            sx={{ color: theme.palette.primary.main, mr: 1 }}
                                                        >
                                                            <Edit2 size={18} />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenModuleDeleteModal(module)}
                                                            sx={{ color: theme.palette.error.main }}
                                                        >
                                                            <Trash2 size={18} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>

                    {/* Pagination */}
                    {filteredModules.length > 0 && moduleTotalPages > 1 && (
                        <Stack spacing={2} alignItems="center" sx={{ mt: 3 }}>
                            <Pagination
                                count={moduleTotalPages}
                                page={moduleCurrentPage}
                                onChange={(e, value) => setModuleCurrentPage(value)}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                            />
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Showing {((moduleCurrentPage - 1) * modulesPerPage) + 1} - {Math.min(moduleCurrentPage * modulesPerPage, filteredModules.length)} of {filteredModules.length} modules
                            </Typography>
                        </Stack>
                    )}
                </Box>

                {/* Dimension Modal */}
                <Dialog
                    open={dimensionModalOpen}
                    onClose={handleCloseDimensionModal}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {dimensionEditMode ? 'Edit Dimension' : 'Add New Dimension'}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                label="Dimension Name"
                                value={dimensionFormData.dimension_name}
                                onChange={(e) => setDimensionFormData({ ...dimensionFormData, dimension_name: e.target.value })}
                                sx={{ mb: 3 }}
                                required
                            />
                            <FormControl fullWidth>
                                <InputLabel>Chart Type (Optional)</InputLabel>
                                <Select
                                    value={dimensionFormData.chart_id}
                                    label="Chart Type (Optional)"
                                    onChange={(e: SelectChangeEvent<number | ''>) => {
                                        const value = e.target.value;
                                        setDimensionFormData({
                                            ...dimensionFormData,
                                            chart_id: value === '' ? '' : Number(value)
                                        });
                                    }}
                                >
                                    <MenuItem value=""><em>-- No Chart --</em></MenuItem>
                                    {chartTypes.map((chart) => (
                                        <MenuItem key={chart.chart_id} value={chart.chart_id}>
                                            {chart.chart_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDimensionModal} disabled={actionLoading['save-dimension']}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveDimension}
                            variant="contained"
                            disabled={!dimensionFormData.dimension_name.trim() || actionLoading['save-dimension']}
                        >
                            {actionLoading['save-dimension'] ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Module Modal */}
                <Dialog
                    open={moduleModalOpen}
                    onClose={handleCloseModuleModal}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {moduleEditMode ? 'Edit Module' : 'Add New Module'}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                label="Module Title"
                                value={moduleFormData.title}
                                onChange={(e) => setModuleFormData({ ...moduleFormData, title: e.target.value })}
                                required
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseModuleModal} disabled={actionLoading['save-module']}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveModule}
                            variant="contained"
                            disabled={!moduleFormData.title.trim() || actionLoading['save-module']}
                        >
                            {actionLoading['save-module'] ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dimension Delete Confirmation Modal */}
                <Dialog
                    open={dimensionDeleteModalOpen}
                    onClose={handleCloseDimensionDeleteModal}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete the dimension <strong>{dimensionToDelete?.dimension_name}</strong>?
                        </Typography>
                        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                            This action cannot be undone. The dimension will be permanently deleted.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDimensionDeleteModal} disabled={actionLoading['delete-dimension']}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDeleteDimension}
                            variant="contained"
                            color="error"
                            disabled={actionLoading['delete-dimension']}
                        >
                            {actionLoading['delete-dimension'] ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Module Delete Confirmation Modal */}
                <Dialog
                    open={moduleDeleteModalOpen}
                    onClose={handleCloseModuleDeleteModal}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete the module <strong>{moduleToDelete?.title}</strong>?
                        </Typography>
                        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                            This action cannot be undone. The module will be permanently deleted.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseModuleDeleteModal} disabled={actionLoading['delete-module']}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDeleteModule}
                            variant="contained"
                            color="error"
                            disabled={actionLoading['delete-module']}
                        >
                            {actionLoading['delete-module'] ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Layout>
    );
};

export default ManageDimensionsModules;
